import { PlayCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import DownloadButton from '../DownloadButton/DownloadButton';
import './Hero.css';

export default function Hero() {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const mouseRef = useRef({ x: -1000, y: -1000, tx: -1000, ty: -1000 });
  const particlesRef = useRef([]);
  const sizeRef = useRef({ w: 0, h: 0 });
  const lastTsRef = useRef(0);

  const brandCanvasRef = useRef(null);
  const brandRafRef = useRef(0);

  // Typing animation state
  const [displayedText, setDisplayedText] = useState('');
  const fullText = 'Design Beyond Limits';

  useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      if (index <= fullText.length) {
        setDisplayedText(fullText.slice(0, index));
        index++;
      } else {
        clearInterval(interval);
      }
    }, 60);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 2D particle field with depth for visual concentration
    const CONFIG = {
      // Depth and perspective
      depth: 600,
      fov: 400,

      // Density / visuals
      density: 10000,
      baseSizeMin: 3,
      baseSizeMax: 7,
      alpha: 0.55,

      // Motion - more alive
      friction: 0.96,
      drift: 0.12,
      returnForce: 0.006,
      orbitSpeed: 0.3,

      // Mouse interaction
      mouseRadius: 180,
      repelForce: 0.4,
      mouseLerp: 0.15,
    };

    let time = 0;

    function resize() {
      const w = window.innerWidth;
      const h = window.innerHeight;
      sizeRef.current = { w, h };
      canvas.width = w;
      canvas.height = h;
    }

    function lerp(a, b, t) {
      return a + (b - a) * t;
    }

    // Project 3D to 2D for visual depth
    function project(particle, size) {
      const scale = CONFIG.fov / (CONFIG.fov + particle.z);
      const sx = particle.x * scale + size.w / 2 * (1 - scale);
      const sy = particle.y * scale + size.h / 2 * (1 - scale);
      return { sx, sy, scale };
    }

    class Particle {
      constructor() {
        const { w, h } = sizeRef.current;
        // Base positions clustered toward center
        this.bx = w * 0.12 + Math.random() * w * 0.76;
        this.by = h * 0.12 + Math.random() * h * 0.76;
        this.z = Math.random() * CONFIG.depth;

        this.x = this.bx;
        this.y = this.by;

        // Velocity for movement
        this.vx = (Math.random() - 0.5) * 0.4;
        this.vy = (Math.random() - 0.5) * 0.4;

        this.size = CONFIG.baseSizeMin + Math.random() * (CONFIG.baseSizeMax - CONFIG.baseSizeMin);
        const shade = Math.round(30 + Math.random() * 50);
        this.color = `rgb(${shade}, ${shade}, ${shade})`;

        // Animation offsets for organic feel
        this.phase = Math.random() * Math.PI * 2;
        this.orbitRadius = 3 + Math.random() * 8;
        this.pulseSpeed = 0.5 + Math.random() * 1.5;
      }

      step(dt) {
        const { w, h } = sizeRef.current;
        const mouse = mouseRef.current;

        // Orbital motion around base position
        const orbitX = Math.cos(time * CONFIG.orbitSpeed + this.phase) * this.orbitRadius;
        const orbitY = Math.sin(time * CONFIG.orbitSpeed + this.phase) * this.orbitRadius;

        // Spring back to base + orbit
        this.vx += (this.bx + orbitX - this.x) * CONFIG.returnForce;
        this.vy += (this.by + orbitY - this.y) * CONFIG.returnForce;

        // Random drift for organic movement
        this.vx += (Math.random() - 0.5) * CONFIG.drift;
        this.vy += (Math.random() - 0.5) * CONFIG.drift;

        // Mouse repel in screen space
        const { sx, sy, scale } = project(this, sizeRef.current);
        const dx = sx - mouse.x;
        const dy = sy - mouse.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;

        if (dist < CONFIG.mouseRadius) {
          const strength = (1 - dist / CONFIG.mouseRadius) ** 1.8;
          const ux = dx / dist;
          const uy = dy / dist;
          const push = (CONFIG.repelForce * strength) / Math.max(0.35, scale);
          this.vx += ux * push;
          this.vy += uy * push;
        }

        // Apply friction and integrate
        const f = Math.pow(CONFIG.friction, dt * 60);
        this.vx *= f;
        this.vy *= f;

        this.x += this.vx * dt * 60;
        this.y += this.vy * dt * 60;

        // Wrap around screen edges
        if (this.x > w + 60) this.x = -60;
        if (this.x < -60) this.x = w + 60;
        if (this.y > h + 60) this.y = -60;
        if (this.y < -60) this.y = h + 60;
      }

      draw() {
        const { sx, sy, scale } = project(this, sizeRef.current);

        // Pulsing size
        const pulse = 1 + Math.sin(time * this.pulseSpeed + this.phase) * 0.15;
        const size = this.size * scale * pulse;
        if (size < 0.5) return;

        // Breathing alpha
        const breathe = 0.85 + Math.sin(time * this.pulseSpeed * 0.7 + this.phase) * 0.15;
        ctx.globalAlpha = CONFIG.alpha * Math.min(1, scale * 1.3) * breathe;
        ctx.fillStyle = this.color;

        // Draw pixel square instead of circle
        ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
      }
    }

    function initParticles() {
      resize();
      const { w, h } = sizeRef.current;
      const count = Math.max(60, Math.floor((w * h) / CONFIG.density));
      particlesRef.current = Array.from({ length: count }, () => new Particle());
    }

    function animate(ts) {
      const { w, h } = sizeRef.current;
      const lastTs = lastTsRef.current || ts;
      const dt = Math.min(0.033, Math.max(0.001, (ts - lastTs) / 1000));
      lastTsRef.current = ts;

      // Increment time for animations
      time += dt;

      // Smooth mouse follow
      const mouse = mouseRef.current;
      mouse.x = lerp(mouse.x, mouse.tx, CONFIG.mouseLerp);
      mouse.y = lerp(mouse.y, mouse.ty, CONFIG.mouseLerp);

      ctx.clearRect(0, 0, w, h);

      const particles = particlesRef.current;
      for (let i = 0; i < particles.length; i++) {
        particles[i].step(dt);
        particles[i].draw();
      }

      rafRef.current = window.requestAnimationFrame(animate);
    }

    function onMouseMove(e) {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.tx = e.clientX - rect.left;
      mouseRef.current.ty = e.clientY - rect.top;
    }

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('resize', initParticles);

    initParticles();
    lastTsRef.current = 0;
    rafRef.current = window.requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('resize', initParticles);
      window.cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    const canvas = brandCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const brand = '<REVTRONE>';

    const state = {
      mouse: { x: -1000, y: -1000, tx: -1000, ty: -1000 },
      particles: [],
      targets: [],
      lastTs: 0,
    };

    const CONFIG = {
      fontFamily: 'G7Gradius11Byte',
      fontSize: 80, // Much bigger for visibility
      letterSpacing: 6,
      sampleStep: 3, // Dense sampling = ~3 lines of particles around each letter
      // particle motion - using lerp, not springs (no bounce possible)
      lerpSpeed: 0.12, // How fast particles move toward target (0-1)
      // Hover interaction - subtle displacement
      repelRadius: 45, // Interaction area
      repelStrength: 6, // How far particles push (in pixels)
      // Lattice connection settings
      connectionDistance: 8, // Tighter connections for denser mesh
      lineWidth: 1,
      lineColor: '#171717',
      nodeSize: 1.5,
      nodeColor: '#171717',
    };

    function lerp(a, b, t) {
      return a + (b - a) * t;
    }

    function resize() {
      const tmp = document.createElement('canvas').getContext('2d');
      if (!tmp) return;
      tmp.font = `${CONFIG.fontSize}px ${CONFIG.fontFamily}, monospace`;

      let width = 0;
      for (let i = 0; i < brand.length; i++) {
        const w = tmp.measureText(brand[i]).width;
        width += w + CONFIG.letterSpacing;
      }

      const height = Math.ceil(CONFIG.fontSize * 1.4);
      const cssW = Math.ceil(width + 20);
      const cssH = Math.ceil(height + 20);

      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;
      canvas.width = Math.floor(cssW * dpr);
      canvas.height = Math.floor(cssH * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function buildTargets() {
      const off = document.createElement('canvas');
      const offCtx = off.getContext('2d');
      if (!offCtx) return;

      const cssW = parseFloat(canvas.style.width || '320') || 320;
      const cssH = parseFloat(canvas.style.height || '56') || 56;

      off.width = Math.floor(cssW * dpr);
      off.height = Math.floor(cssH * dpr);
      offCtx.setTransform(dpr, 0, 0, dpr, 0, 0);

      offCtx.clearRect(0, 0, cssW, cssH);
      offCtx.fillStyle = '#000';
      offCtx.textBaseline = 'middle';
      offCtx.textAlign = 'left';
      offCtx.font = `${CONFIG.fontSize}px ${CONFIG.fontFamily}, monospace`;

      let x = 10;
      const y = cssH / 2 + 2;
      for (let i = 0; i < brand.length; i++) {
        const ch = brand[i];
        offCtx.fillText(ch, x, y);
        x += offCtx.measureText(ch).width + CONFIG.letterSpacing;
      }

      const img = offCtx.getImageData(0, 0, Math.floor(cssW * dpr), Math.floor(cssH * dpr));
      const data = img.data;
      const targets = [];

      const step = CONFIG.sampleStep;
      for (let yy = 0; yy < cssH; yy += step) {
        for (let xx = 0; xx < cssW; xx += step) {
          const ix = (Math.floor(yy * dpr) * Math.floor(cssW * dpr) + Math.floor(xx * dpr)) * 4;
          const a = data[ix + 3];
          if (a > 40) {
            targets.push({ x: xx, y: yy });
          }
        }
      }

      state.targets = targets;

      // Create particles - higher cap for denser lattice
      const max = Math.min(2000, targets.length);
      state.particles = Array.from({ length: max }, (_, i) => {
        const t = targets[i];
        return {
          x: t.x,
          y: t.y,
          tx: t.x,
          ty: t.y,
        };
      });
    }

    function step(ts) {
      const cssW = parseFloat(canvas.style.width || '320') || 320;
      const cssH = parseFloat(canvas.style.height || '56') || 56;

      // smooth mouse
      state.mouse.x = lerp(state.mouse.x, state.mouse.tx, 0.15);
      state.mouse.y = lerp(state.mouse.y, state.mouse.ty, 0.15);

      ctx.clearRect(0, 0, cssW, cssH);

      const particles = state.particles;

      // Update particle positions using direct lerp (NO velocity = NO bounce)
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        // Calculate target position (base position + mouse displacement)
        let goalX = p.tx;
        let goalY = p.ty;

        // Check mouse proximity and add displacement
        const dx = p.tx - state.mouse.x;
        const dy = p.ty - state.mouse.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;

        if (dist < CONFIG.repelRadius) {
          // Calculate displacement amount (linear falloff)
          const strength = 1 - dist / CONFIG.repelRadius;
          const ux = dx / dist;
          const uy = dy / dist;
          // Displace the goal position, not add velocity
          goalX += ux * CONFIG.repelStrength * strength;
          goalY += uy * CONFIG.repelStrength * strength;
        }

        // Smoothly lerp toward goal (no springs, no bounce, just smooth movement)
        p.x = lerp(p.x, goalX, CONFIG.lerpSpeed);
        p.y = lerp(p.y, goalY, CONFIG.lerpSpeed);
      }

      // Draw lattice connections FIRST (behind nodes)
      ctx.strokeStyle = CONFIG.lineColor;
      ctx.lineWidth = CONFIG.lineWidth;

      for (let i = 0; i < particles.length; i++) {
        const p1 = particles[i];
        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const dx = p2.x - p1.x;
          const dy = p2.y - p1.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < CONFIG.connectionDistance) {
            // Fade line based on distance
            const alpha = 1 - (dist / CONFIG.connectionDistance);
            ctx.globalAlpha = alpha * 0.7;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }
      }

      // Draw nodes on top
      ctx.fillStyle = CONFIG.nodeColor;
      ctx.globalAlpha = 1;
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        ctx.beginPath();
        ctx.arc(p.x, p.y, CONFIG.nodeSize, 0, Math.PI * 2);
        ctx.fill();
      }

      brandRafRef.current = window.requestAnimationFrame(step);
    }

    function onMouseMove(e) {
      const rect = canvas.getBoundingClientRect();
      // Calculate mouse position in CSS space (not canvas pixel space)
      const scaleX = parseFloat(canvas.style.width) / rect.width;
      const scaleY = parseFloat(canvas.style.height) / rect.height;
      state.mouse.tx = (e.clientX - rect.left) * scaleX;
      state.mouse.ty = (e.clientY - rect.top) * scaleY;
    }

    function onMouseLeave() {
      state.mouse.tx = -1000;
      state.mouse.ty = -1000;
    }

    // Wait for the font so the particle targets match the actual glyphs
    const fontPromise =
      document.fonts?.load?.(`${CONFIG.fontSize}px ${CONFIG.fontFamily}`) ?? Promise.resolve();

    let cancelled = false;
    fontPromise
      .catch(() => undefined)
      .finally(() => {
        if (cancelled) return;
        resize();
        buildTargets();
        state.lastTs = 0;
        brandRafRef.current = window.requestAnimationFrame(step);
      });

    window.addEventListener('resize', () => {
      resize();
      buildTargets();
    });
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseleave', onMouseLeave);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(brandRafRef.current);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  return (
    <header id="top" className="hero">
      <canvas ref={canvasRef} className="hero__canvas" />

      <div className="hero__content">
        {/* <div className="hero__badge" aria-label="Public beta">
          <span className="hero__ping">
            <span className="hero__pingRing" />
            <span className="hero__pingDot" />
          </span>
          v1.0 Public Beta is Live
        </div> */}

        <div className="heroBrand" aria-label="Revitrone brand">
          <canvas className="heroBrand__canvas" ref={brandCanvasRef} aria-label="REVITRONE" />
        </div>

        <h1 className="hero__title hero__title--typed" aria-label="Design Beyond Limits">
          {displayedText}
          <span className="hero__cursor">|</span>
        </h1>

        {/* <p className="hero__subtitle">
          Revitron is the AI-powered copilot for modern architecture. Generate BIM models,
          optimize layouts, and render photorealistic concepts in real-time.
        </p> */}

        <div className="hero__buttons">
          <DownloadButton variant="hero" showBothPlatforms={false} />
          <button className="hero__secondaryBtn" type="button">
            <PlayCircle className="hero__btnIcon" aria-hidden="true" />
            Watch Demo
          </button>
        </div>
      </div>

      {/* <div className="hero__scrollIndicator" aria-hidden="true">
        <MouseIcon className="hero__scrollIcon" />
      </div> */}
    </header>
  );
}

